// Call notes: response readers, the author's 15-minute change window, error copy and the in-call draft
// kept in this tab (UX spec 6.2.6: notes autosave locally every 2 seconds and reach the server only when
// the person saves). Pure: no React.
import type { CallNote } from '../../lib/api.ts';
import { isApiRequestError } from '../../lib/api.ts';
import { describeError } from '../../lib/errorCopy.ts';
import { isRecord, unwrap } from '../blocklist/model.ts';

export const NOTE_MAX = 10_000;
/** The API lets an author edit or delete their own note for 15 minutes (NOTE_EDIT_WINDOW_SECONDS). */
export const NOTE_EDIT_WINDOW_MS = 15 * 60_000;
export const DRAFT_SAVE_MS = 2000;

const ID = /^[A-Za-z0-9_.:-]{1,128}$/;
const time = (value: unknown) => (typeof value === 'string' && !Number.isNaN(Date.parse(value)) ? value : null);

export function readNote(value: unknown): CallNote | null {
  const v = unwrap(value);
  if (!isRecord(v) || typeof v.id !== 'string' || !ID.test(v.id) || typeof v.body !== 'string') return null;
  const created = time(v.created_at);
  if (!created) return null;
  const author = isRecord(v.author) ? v.author : {};
  return {
    id: v.id,
    body: v.body.replace(/\u0000/g, '').slice(0, NOTE_MAX),
    author: { id: typeof author.id === 'string' ? author.id : '', name: typeof author.name === 'string' && author.name.trim() ? author.name.trim().slice(0, 200) : 'Someone in the workspace' },
    created_at: created,
    updated_at: time(v.updated_at) ?? created,
  };
}

/** Oldest first, as the API sends them. Null when the body isn't a list of notes. */
export function readNotes(body: unknown): CallNote[] | null {
  const v = unwrap(body);
  if (!isRecord(v) || !Array.isArray(v.items)) return null;
  return v.items.flatMap(raw => { const n = readNote(raw); return n ? [n] : []; });
}

export function isOwnNote(note: CallNote, userId: string): boolean {
  return !!note.author.id && note.author.id === userId;
}

/** The author may still change it. The server decides; this only hides buttons that would fail. */
export function canChangeNote(note: CallNote, userId: string, now = Date.now()): boolean {
  return isOwnNote(note, userId) && now - Date.parse(note.created_at) < NOTE_EDIT_WINDOW_MS;
}

export function wasEdited(note: CallNote): boolean {
  return Date.parse(note.updated_at) - Date.parse(note.created_at) > 1000;
}

export function noteErrorText(error: unknown, fallback: string): string {
  if (isApiRequestError(error)) {
    if (error.code === 'note_edit_window_closed') return 'Notes can only be changed for 15 minutes after they’re added. Add a follow-up note instead.';
    if (error.code === 'permission_denied') return 'Only the person who wrote a note can change it.';
    if (error.code === 'not_found') return 'This note was deleted, or the call isn’t shared with you.';
    if (error.code === 'invalid_input') return `Notes need some text, up to ${NOTE_MAX.toLocaleString('en-US')} characters.`;
  }
  const view = describeError(error, fallback);
  return [view.title, view.detail, view.requestId ? `Reference ${view.requestId}.` : ''].filter(Boolean).join(' ');
}

// ---- In-call drafts --------------------------------------------------------------------------------------

export interface DraftStore {
  get: (key: string) => string | null;
  set: (key: string, value: string) => void;
  remove: (key: string) => void;
}

/** Per tab, so a draft never outlives the browser tab or reaches another person signing in elsewhere. */
export function draftKey(tenantId: string | null | undefined, userId: string, callId: string): string {
  return `sidetone.note-draft.${tenantId ?? 'none'}.${userId}.${callId}`;
}

export function sessionDrafts(): DraftStore {
  const storage = (() => {
    try {
      return typeof window === 'undefined' ? null : window.sessionStorage;
    } catch {
      return null;
    }
  })();
  return {
    get: key => { try { return storage?.getItem(key) ?? null; } catch { return null; } },
    set: (key, value) => { try { storage?.setItem(key, value.slice(0, NOTE_MAX)); } catch { /* Storage full or blocked: the field still holds the text. */ } },
    remove: key => { try { storage?.removeItem(key); } catch { /* Nothing to remove. */ } },
  };
}
